// pages/strategic-playbook/tools/what-would-thinker-do.js
// ════════════════════════════════════════════════════
// WHAT WOULD [THINKER] DO
// ════════════════════════════════════════════════════
// Apply any of the 8 thinker dossiers' operating systems
// to the user's specific situation. Each thinker's voice
// is distinct because the API loads their full dossier
// as context.
// ════════════════════════════════════════════════════

import { useState } from 'react';
import Head from 'next/head';
import Link from 'next/link';
import { DOSSIERS } from '../../../data/strategic-playbook/dossiers';

const TIME_HORIZONS = [
  { id: 'short', label: 'Short-term', detail: 'Days to weeks' },
  { id: 'medium', label: 'Medium-term', detail: 'Months to a year' },
  { id: 'long', label: 'Long-term', detail: 'Multi-year' },
  { id: 'multi-decade', label: 'Multi-decade', detail: '10+ years' },
];

export default function WhatWouldThinkerDoTool() {
  // Filter thinkers from the central library
  const thinkers = DOSSIERS.filter((d) => d.type === 'thinker');

  const [thinkerId, setThinkerId] = useState(thinkers[0]?.id || '');
  const [situation, setSituation] = useState('');
  const [timeHorizon, setTimeHorizon] = useState('medium');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [result, setResult] = useState(null);

  const selectedThinker = thinkers.find((t) => t.id === thinkerId);

  async function generate() {
    if (!thinkerId) {
      setError('Pick a thinker first.');
      return;
    }
    if (situation.trim().length < 10) {
      setError('Describe the situation in more detail (at least 10 characters).');
      return;
    }
    setLoading(true);
    setError(null);
    setResult(null);

    try {
      const res = await fetch('/api/strategic-playbook/what-would-thinker-do', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          thinkerId,
          situation,
          timeHorizon: TIME_HORIZONS.find((t) => t.id === timeHorizon)?.label || timeHorizon,
        }),
      });
      const data = await res.json();
      if (!res.ok) {
        throw new Error(data.error || 'Failed to generate');
      }
      setResult(data);
    } catch (err) {
      setError(err.message || 'Something went wrong. Try again.');
    } finally {
      setLoading(false);
    }
  }

  function reset() {
    setResult(null);
    setError(null);
  }

  return (
    <div className="sp-root">
      <Head>
        <title>What Would [Thinker] Do · Strategic Intelligence</title>
      </Head>

      <div className="sp-page">
        <ToolTopbar />

        <div style={{ marginBottom: 32 }}>
          <Link href="/strategic-playbook/tools" className="sp-reader-back" style={{ marginBottom: 16 }}>
            ← Tools
          </Link>
          <div style={{ fontFamily: 'var(--sp-font-mono)', fontSize: 10, textTransform: 'uppercase', letterSpacing: '0.20em', color: 'var(--sp-aged-brass)', marginBottom: 14 }}>
            Strategic Framing · Generator
          </div>
          <h1 style={{ fontFamily: 'var(--sp-font-serif)', fontWeight: 500, fontSize: 32, lineHeight: 1.2, color: 'var(--sp-warm-cream)', margin: '0 0 14px 0', letterSpacing: '-0.01em' }}>
            What Would [Thinker] Do
          </h1>
          <p style={{ fontSize: 14, lineHeight: 1.65, color: 'rgba(232, 221, 196, 0.78)', maxWidth: 720, margin: 0 }}>
            Pick one of 8 elite thinkers. Describe your situation. Get their operating
            system applied — what they&apos;d see, what they&apos;d reject, the questions they&apos;d
            force you to answer, and one concrete next step in their voice. Each thinker
            answers from their full dossier as context, not generic advice.
          </p>
        </div>

        {/* Thinker picker */}
        {!result && (
          <div style={{ marginBottom: 24 }}>
            <div style={{ fontFamily: 'var(--sp-font-mono)', fontSize: 10, textTransform: 'uppercase', letterSpacing: '0.18em', color: 'var(--sp-aged-brass)', marginBottom: 14 }}>
              Choose a Thinker
            </div>
            <div
              style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))',
                gap: 10,
              }}
            >
              {thinkers.map((t) => (
                <ThinkerPickerCard
                  key={t.id}
                  thinker={t}
                  selected={thinkerId === t.id}
                  onSelect={() => setThinkerId(t.id)}
                />
              ))}
            </div>
          </div>
        )}

        {/* Input form (also shown above results to enable re-running) */}
        {!result && (
          <div
            style={{
              padding: '28px',
              background: 'var(--sp-soft-charcoal)',
              border: '1px solid var(--sp-divider)',
              borderRadius: 'var(--sp-radius-card)',
              marginBottom: 24,
            }}
          >
            <h3 style={{ fontFamily: 'var(--sp-font-mono)', fontSize: 10, textTransform: 'uppercase', letterSpacing: '0.18em', color: 'var(--sp-aged-brass)', margin: '0 0 22px 0' }}>
              Your Situation {selectedThinker && <span style={{ color: 'rgba(232, 221, 196, 0.55)', marginLeft: 8 }}>· asking {selectedThinker.thinkerName}</span>}
            </h3>

            <textarea
              value={situation}
              onChange={(e) => setSituation(e.target.value)}
              placeholder={`Describe what you're facing. Example: 'My SaaS company hit $2M ARR but growth has flatlined for 6 months. Considering raising a $5M Series A vs bootstrapping further. Founder is solo, runway is 18 months at current burn.'`}
              rows={6}
              style={{
                width: '100%',
                padding: '14px 18px',
                background: 'var(--sp-charcoal-black)',
                border: '1px solid var(--sp-divider)',
                borderRadius: 'var(--sp-radius)',
                color: 'var(--sp-warm-cream)',
                fontFamily: 'var(--sp-font-body)',
                fontSize: 14,
                lineHeight: 1.6,
                resize: 'vertical',
                outline: 'none',
                marginBottom: 18,
              }}
            />

            <div style={{ marginBottom: 18 }}>
              <div style={{ fontFamily: 'var(--sp-font-mono)', fontSize: 10, textTransform: 'uppercase', letterSpacing: '0.14em', color: 'rgba(232, 221, 196, 0.55)', marginBottom: 8 }}>
                Time Horizon
              </div>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                {TIME_HORIZONS.map((t) => (
                  <button
                    key={t.id}
                    onClick={() => setTimeHorizon(t.id)}
                    style={{
                      padding: '6px 14px',
                      background: timeHorizon === t.id ? 'var(--sp-brass-bg)' : 'transparent',
                      border: `1px solid ${timeHorizon === t.id ? 'var(--sp-aged-brass)' : 'var(--sp-divider)'}`,
                      borderRadius: 'var(--sp-radius)',
                      cursor: 'pointer',
                      fontSize: 12,
                      color: timeHorizon === t.id ? 'var(--sp-aged-brass)' : 'rgba(232, 221, 196, 0.65)',
                      fontFamily: 'var(--sp-font-mono)',
                      letterSpacing: '0.08em',
                      textTransform: 'uppercase',
                    }}
                  >
                    {t.label}
                  </button>
                ))}
              </div>
            </div>

            <div style={{ display: 'flex', gap: 12, alignItems: 'center', flexWrap: 'wrap' }}>
              <button
                onClick={generate}
                disabled={loading || situation.trim().length < 10 || !thinkerId}
                style={{
                  padding: '12px 24px',
                  background: loading || situation.trim().length < 10 ? 'transparent' : 'var(--sp-brass-bg)',
                  border: `1px solid ${loading || situation.trim().length < 10 ? 'var(--sp-divider)' : 'var(--sp-aged-brass)'}`,
                  color: loading || situation.trim().length < 10 ? 'rgba(232, 221, 196, 0.40)' : 'var(--sp-aged-brass)',
                  borderRadius: 'var(--sp-radius-card)',
                  fontFamily: 'var(--sp-font-mono)',
                  fontSize: 11,
                  textTransform: 'uppercase',
                  letterSpacing: '0.16em',
                  cursor: loading || situation.trim().length < 10 ? 'not-allowed' : 'pointer',
                }}
              >
                {loading
                  ? 'Asking...'
                  : selectedThinker
                  ? `Ask ${selectedThinker.thinkerName} →`
                  : 'Pick a thinker first'}
              </button>
              {error && (
                <div style={{ fontSize: 12, color: 'var(--sp-oxblood)', fontFamily: 'var(--sp-font-mono)', letterSpacing: '0.04em' }}>
                  {error}
                </div>
              )}
            </div>
          </div>
        )}

        {/* Loading state */}
        {loading && (
          <div
            style={{
              padding: '48px',
              background: 'var(--sp-soft-charcoal)',
              border: '1px solid var(--sp-divider)',
              borderRadius: 'var(--sp-radius-card)',
              textAlign: 'center',
              fontFamily: 'var(--sp-font-serif)',
              fontStyle: 'italic',
              color: 'var(--sp-aged-brass)',
              fontSize: 16,
            }}
          >
            Channeling {selectedThinker?.thinkerName}&apos;s framework...
            <div style={{ fontFamily: 'var(--sp-font-mono)', fontStyle: 'normal', fontSize: 11, color: 'rgba(232, 221, 196, 0.50)', marginTop: 8, letterSpacing: '0.10em', textTransform: 'uppercase' }}>
              Loading dossier as context · {selectedThinker?.coreIdea}
            </div>
          </div>
        )}

        {/* Results */}
        {result && !loading && <ResultsView result={result} onReset={reset} />}
      </div>
    </div>
  );
}

function ThinkerPickerCard({ thinker, selected, onSelect }) {
  return (
    <button
      onClick={onSelect}
      style={{
        textAlign: 'left',
        padding: '14px 16px',
        background: selected ? 'var(--sp-brass-bg)' : 'var(--sp-soft-charcoal)',
        border: `1px solid ${selected ? 'var(--sp-aged-brass)' : 'var(--sp-divider)'}`,
        borderRadius: 'var(--sp-radius-card)',
        cursor: 'pointer',
        position: 'relative',
        overflow: 'hidden',
        transition: 'border-color 0.2s, background 0.2s',
      }}
    >
      <div
        style={{
          fontFamily: 'var(--sp-font-serif)',
          fontWeight: 500,
          fontSize: 16,
          lineHeight: 1.2,
          color: selected ? 'var(--sp-warm-cream)' : 'rgba(232, 221, 196, 0.85)',
          marginBottom: 4,
          letterSpacing: '-0.005em',
        }}
      >
        {thinker.thinkerName}
      </div>
      <div
        style={{
          fontFamily: 'var(--sp-font-serif)',
          fontStyle: 'italic',
          fontSize: 12,
          lineHeight: 1.4,
          color: selected ? 'var(--sp-aged-brass)' : 'rgba(232, 221, 196, 0.55)',
        }}
      >
        {thinker.coreIdea}
      </div>
    </button>
  );
}

function ResultsView({ result, onReset }) {
  return (
    <div>
      {/* Header card */}
      <div
        style={{
          padding: '32px 36px',
          background: 'linear-gradient(135deg, var(--sp-card-charcoal) 0%, var(--sp-soft-charcoal) 100%)',
          border: '1px solid var(--sp-aged-brass)',
          borderRadius: 'var(--sp-radius-card)',
          marginBottom: 20,
          position: 'relative',
        }}
      >
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 16 }}>
          <div>
            <div style={{ fontFamily: 'var(--sp-font-mono)', fontSize: 10, textTransform: 'uppercase', letterSpacing: '0.18em', color: 'var(--sp-aged-brass)', marginBottom: 10 }}>
              Channeling
            </div>
            <h2 style={{ fontFamily: 'var(--sp-font-serif)', fontWeight: 500, fontSize: 28, lineHeight: 1.15, color: 'var(--sp-warm-cream)', margin: '0 0 6px 0', letterSpacing: '-0.01em' }}>
              {result.thinkerName}
            </h2>
            <div style={{ fontFamily: 'var(--sp-font-serif)', fontStyle: 'italic', fontSize: 16, color: 'var(--sp-aged-brass)' }}>
              {result.coreIdea}
            </div>
          </div>
          <button
            onClick={onReset}
            style={{
              padding: '8px 14px',
              background: 'transparent',
              border: '1px solid var(--sp-divider)',
              borderRadius: 'var(--sp-radius)',
              color: 'rgba(232, 221, 196, 0.65)',
              fontFamily: 'var(--sp-font-mono)',
              fontSize: 10,
              textTransform: 'uppercase',
              letterSpacing: '0.10em',
              cursor: 'pointer',
            }}
          >
            ← New Question
          </button>
        </div>

        {/* Quote */}
        {result.relevantQuote && (
          <div
            style={{
              fontFamily: 'var(--sp-font-serif)',
              fontStyle: 'italic',
              fontSize: 16,
              lineHeight: 1.55,
              color: 'var(--sp-warm-cream)',
              borderLeft: '2px solid var(--sp-aged-brass)',
              paddingLeft: 16,
              marginTop: 16,
            }}
          >
            &ldquo;{result.relevantQuote}&rdquo;
          </div>
        )}
      </div>

      {/* What they see */}
      <ResultBlock
        eyebrow="What They See"
        body={result.whatTheySee}
        accent="var(--sp-aged-brass)"
      />

      {/* What they reject */}
      <ResultBlock
        eyebrow="What They Reject"
        body={result.whatTheyReject}
        accent="var(--sp-oxblood)"
      />

      {/* Questions */}
      <div
        style={{
          padding: '24px 28px',
          background: 'var(--sp-soft-charcoal)',
          border: '1px solid var(--sp-divider)',
          borderRadius: 'var(--sp-radius-card)',
          marginBottom: 16,
        }}
      >
        <div style={{ fontFamily: 'var(--sp-font-mono)', fontSize: 10, textTransform: 'uppercase', letterSpacing: '0.18em', color: 'var(--sp-aged-brass)', marginBottom: 14 }}>
          Questions They&apos;d Force You to Answer
        </div>
        <ol style={{ margin: 0, paddingLeft: 22 }}>
          {(result.whatTheyAsk || []).map((q, i) => (
            <li
              key={i}
              style={{
                fontFamily: 'var(--sp-font-serif)',
                fontStyle: 'italic',
                fontSize: 15,
                lineHeight: 1.6,
                color: 'var(--sp-warm-cream)',
                marginBottom: 8,
              }}
            >
              {q}
            </li>
          ))}
        </ol>
      </div>

      {/* How they approach */}
      <ResultBlock
        eyebrow="How They'd Approach This"
        body={result.howTheyApproach}
        accent="var(--sp-forest-moss)"
      />

      {/* Concrete next step */}
      <div
        style={{
          padding: '24px 28px',
          background: 'rgba(58, 84, 71, 0.18)',
          border: '1px solid rgba(58, 84, 71, 0.45)',
          borderRadius: 'var(--sp-radius-card)',
          marginBottom: 16,
        }}
      >
        <div style={{ fontFamily: 'var(--sp-font-mono)', fontSize: 10, textTransform: 'uppercase', letterSpacing: '0.18em', color: 'var(--sp-forest-moss)', marginBottom: 12 }}>
          ✓ Concrete Next Step (This Week)
        </div>
        <div
          style={{
            fontFamily: 'var(--sp-font-serif)',
            fontSize: 16,
            lineHeight: 1.6,
            color: 'var(--sp-warm-cream)',
          }}
        >
          {result.concreteNextStep}
        </div>
      </div>

      {/* Honest read */}
      <div
        style={{
          padding: '24px 28px',
          background: 'rgba(74, 31, 31, 0.10)',
          border: '1px solid rgba(74, 31, 31, 0.30)',
          borderRadius: 'var(--sp-radius-card)',
          marginBottom: 20,
        }}
      >
        <div style={{ fontFamily: 'var(--sp-font-mono)', fontSize: 10, textTransform: 'uppercase', letterSpacing: '0.18em', color: 'var(--sp-oxblood)', marginBottom: 12 }}>
          Honest Read
        </div>
        <div
          style={{
            fontFamily: 'var(--sp-font-serif)',
            fontStyle: 'italic',
            fontSize: 15,
            lineHeight: 1.65,
            color: 'var(--sp-warm-cream)',
          }}
        >
          {result.honestRead}
        </div>
      </div>

      {/* Connected dossier link */}
      <div style={{ fontSize: 12, color: 'rgba(232, 221, 196, 0.55)' }}>
        <span style={{ fontFamily: 'var(--sp-font-mono)', fontSize: 10, letterSpacing: '0.14em', textTransform: 'uppercase', color: 'rgba(232, 221, 196, 0.45)', marginRight: 8 }}>
          Read the full dossier:
        </span>
        <Link href={`/strategic-playbook/${result.thinkerId}`} style={{ color: 'var(--sp-aged-brass)' }}>
          {result.thinkerName} — {result.coreIdea}
        </Link>
      </div>
    </div>
  );
}

function ResultBlock({ eyebrow, body, accent }) {
  return (
    <div
      style={{
        padding: '24px 28px',
        background: 'var(--sp-soft-charcoal)',
        border: '1px solid var(--sp-divider)',
        borderRadius: 'var(--sp-radius-card)',
        marginBottom: 16,
        position: 'relative',
        overflow: 'hidden',
      }}
    >
      <div
        style={{
          position: 'absolute',
          top: 0,
          left: 0,
          width: 3,
          height: '100%',
          background: accent,
          opacity: 0.7,
        }}
      />
      <div style={{ paddingLeft: 12 }}>
        <div style={{ fontFamily: 'var(--sp-font-mono)', fontSize: 10, textTransform: 'uppercase', letterSpacing: '0.18em', color: accent, marginBottom: 12 }}>
          {eyebrow}
        </div>
        <div
          style={{
            fontFamily: 'var(--sp-font-serif)',
            fontSize: 15,
            lineHeight: 1.65,
            color: 'var(--sp-warm-cream)',
          }}
        >
          {body}
        </div>
      </div>
    </div>
  );
}

function ToolTopbar() {
  return (
    <div className="sp-topbar">
      <div>
        <h1>Strategic Intelligence</h1>
        <div className="sp-topbar-meta">Tools · What Would [Thinker] Do</div>
      </div>
      <nav className="sp-topbar-nav">
        <Link href="/strategic-playbook">Dashboard</Link>
        <Link href="/strategic-playbook/library">Library</Link>
        <Link href="/strategic-playbook/tools" className="sp-active">Tools</Link>
        <Link href="/strategic-playbook/decisions">Decisions</Link>
        <Link href="/strategic-playbook/simulate">Simulate</Link>
      </nav>
    </div>
  );
}
